import { useEffect, useState } from "react";
import { api, formatApiError, getStoredToken, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Download, FileText, FileSpreadsheet, BarChart3, FileSignature, Mail, Send, Map, Archive, RefreshCw, GitCommit, AlertTriangle, History, Sunrise, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { GrantReportEmailModal } from "@/components/GrantReportEmailModal";

const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleString("en-US", { month: "long", year: "numeric" }),
    });
  }
  return out;
};

export default function Reports() {
  const { user, can } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grantMonth, setGrantMonth] = useState(monthOptions()[0].value);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const canEmailExternal = can("grant_reports.email_external");

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const exportFile = async (path, filename) => {
    try {
      const token = getStoredToken();
      const res = await fetch(`${API}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // iOS Safari needs the anchor to be in the DOM before .click() fires,
      // and the object URL must outlive the click long enough for the browser
      // to start the download — hence the small revoke delay.
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
      toast.success("Export ready");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="hw-eyebrow mb-2">Impact & insight</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Reports</h1>
        <p className="text-[#334155] mt-2">Download summaries to share with funders, partners, and the board.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="reports-quicklinks">
        <Link
          to="/reports/assessments"
          data-testid="reports-link-assessments"
          className="group bg-white border border-[#E5E5E0] rounded-2xl p-4 hover:border-[#1E3A8A] transition-colors"
        >
          <div className="flex items-center gap-2 text-[#1E3A8A]">
            <BarChart3 className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-wider">Public assessments</span>
          </div>
          <div className="font-display text-lg font-semibold text-[#0F172A] mt-1.5 group-hover:text-[#1E3A8A]">HCWI dashboard →</div>
          <div className="text-xs text-[#334155] mt-1">
            Anonymous community wellness data, filters, distributions, and CSV/JSON exports.
          </div>
        </Link>
        <Link
          to="/reports/geo-gap"
          data-testid="reports-link-geo-gap"
          className="group bg-white border border-[#E5E5E0] rounded-2xl p-4 hover:border-[#1E3A8A] transition-colors"
        >
          <div className="flex items-center gap-2 text-[#1E3A8A]">
            <Map className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-wider">Coverage</span>
          </div>
          <div className="font-display text-lg font-semibold text-[#0F172A] mt-1.5 group-hover:text-[#1E3A8A]">Geo gap →</div>
          <div className="text-xs text-[#334155] mt-1">
            Heatmap of care-seeker zones vs. partner resource coverage.
          </div>
        </Link>
      </div>

      <TrendsSection />

      <Link
        to="/reports/geo-gap"
        data-testid="link-geo-gap"
        className="block bg-white border border-[#E5E5E0] rounded-2xl p-6 hover:border-[#1E3A8A] hw-card-hover"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#E0E7FF] text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
            <Map className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-semibold text-[#0F172A]">
              Geographic gap analysis
            </h2>
            <p className="text-sm text-[#334155] mt-1">
              Interactive NC map shaded by Care Seekers, inbound demand, or
              Resource coverage. Spot underserved counties at a glance.
            </p>
          </div>
          <span className="text-xs text-[#1E3A8A] font-medium self-center">Open →</span>
        </div>
      </Link>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ExportCard
          icon={FileText}
          title="Dashboard PDF"
          description="Complete impact summary, formatted for sharing."
          onClick={() => exportFile("/exports/dashboard.pdf", "harborwell_report.pdf")}
          testId="export-dashboard-pdf"
        />
        <ExportCard
          icon={FileSpreadsheet}
          title="Care Seekers CSV"
          description="Full Care Seeker list with profile fields."
          onClick={() => exportFile("/exports/care-seekers.csv", "care_seekers.csv")}
          testId="export-care-seekers-csv"
        />
        <ExportCard
          icon={FileSpreadsheet}
          title="Referrals CSV"
          description="All referrals with status & completion data."
          onClick={() => exportFile("/exports/referrals.csv", "referrals.csv")}
          testId="export-referrals-csv"
        />
        <ExportCard
          icon={FileSignature}
          title="Capability summary PDF"
          description="Branded one-pager describing every HarborWell Connect feature — for partner meetings, grant appendices, and staff onboarding."
          onClick={() => exportFile("/exports/feature-summary.pdf", "HarborWell-Connect-Capability-Summary.pdf")}
          testId="export-feature-summary-pdf"
        />
        <ExportCard
          icon={FileSignature}
          title="Production Capabilities Register"
          description="Full enterprise register — every production module organized by domain (Executive Intelligence, Compliance & Governance, Partner Portal, API Platform, Trust Center, Partner Recovery, AI, SSO, Security). Board- and auditor-ready."
          onClick={() => exportFile("/exports/production-capabilities-register.pdf", "HarborWell-Production-Capabilities-Register.pdf")}
          testId="export-production-capabilities-register-pdf"
        />
      </div>

      {isAdmin && <ExecutiveDailyBrief exportFile={exportFile} />}

      {isAdmin && <DocumentationArchive exportFile={exportFile} />}

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8" data-testid="grant-report-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
            <FileSignature className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-xl font-semibold text-[#0F172A]">Monthly Grant Report</h2>
            <p className="text-sm text-[#334155] mt-1 mb-5">
              A funder-ready PDF: new Care Seekers, hotline volume, wellness kits, urgent follow-ups,
              top counties, top support categories — all calendar-month bounded.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1 max-w-xs">
                <label className="block text-xs font-medium text-[#334155] mb-2 uppercase tracking-wider">
                  Reporting month
                </label>
                <select
                  value={grantMonth}
                  onChange={(e) => setGrantMonth(e.target.value)}
                  data-testid="grant-month-select"
                  className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] text-[#0F172A]"
                >
                  {monthOptions().map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() =>
                  exportFile(
                    `/exports/grant-report.pdf?month=${grantMonth}`,
                    `harborwell_grant_report_${grantMonth}.pdf`,
                  )
                }
                data-testid="export-grant-report-pdf"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-medium hover:opacity-90 transition-opacity"
              >
                <Download className="w-4 h-4" strokeWidth={2} />
                Download PDF
              </button>
              {canEmailExternal && (
                <button
                  type="button"
                  onClick={() => setEmailModalOpen(true)}
                  data-testid="grant-report-email-recipient-open"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#1E3A8A] text-[#1E3A8A] font-medium hover:bg-[#F9F8F6] transition-colors"
                >
                  <Mail className="w-4 h-4" strokeWidth={2} />
                  Email to recipient
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <GrantReportEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        month={grantMonth}
        monthLabel={
          monthOptions().find((m) => m.value === grantMonth)?.label ?? grantMonth
        }
      />

      {isAdmin && (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8" data-testid="email-jobs-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#7E937D] text-white flex items-center justify-center flex-shrink-0">
              <Mail className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-xl font-semibold text-[#0F172A]">Email jobs</h2>
              <p className="text-sm text-[#334155] mt-1 mb-5">
                Automated emails run daily at 7am ET (digest) and on the 1st of each month (grant report).
                Use these buttons to trigger them on demand for testing or special updates.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={async () => {
                    setSendingDigest(true);
                    try {
                      const { data } = await api.post("/admin/run-digest");
                      toast.success(`Daily digest sent — ${data.sent} of ${data.total_users} users.`);
                    } catch (e) {
                      toast.error(formatApiError(e));
                    } finally { setSendingDigest(false); }
                  }}
                  disabled={sendingDigest}
                  data-testid="send-digest-now"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  <Send className="w-4 h-4" strokeWidth={2} />
                  {sendingDigest ? "Sending…" : "Send daily digest now"}
                </button>
                <button
                  onClick={async () => {
                    setSendingReport(true);
                    try {
                      const { data } = await api.post("/admin/run-grant-report");
                      toast.success(`${data.month} report emailed to ${data.sent} of ${data.recipients} recipients.`);
                    } catch (e) {
                      toast.error(formatApiError(e));
                    } finally { setSendingReport(false); }
                  }}
                  disabled={sendingReport}
                  data-testid="send-grant-report-now"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#7E937D] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  <Send className="w-4 h-4" strokeWidth={2} />
                  {sendingReport ? "Sending…" : "Email last month's grant report"}
                </button>
              </div>
              <p className="text-xs text-[#1E3A8A] mt-3">
                Recipients default to all active admins & executive directors. Override via
                <code className="bg-[#F9F8F6] px-1.5 py-0.5 rounded font-mono mx-1">GRANT_REPORT_RECIPIENTS</code>
                env var.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-6 h-6 text-[#1E3A8A]" />
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">At-a-glance metrics</h2>
        </div>
        {loading ? (
          <div className="text-center py-8 text-[#1E3A8A]">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricBox label="Care Seekers" value={stats?.total_care_seekers ?? 0} />
            <MetricBox label="Active Care Seekers" value={stats?.active_care_seekers ?? 0} />
            <MetricBox label="Referrals Completed" value={stats?.referrals_completed ?? 0} />
            <MetricBox label="Referrals Pending" value={stats?.referrals_pending ?? 0} />
            <MetricBox label="Maternal Visits" value={stats?.maternal_visits ?? 0} />
            <MetricBox label="Outreach Events" value={stats?.outreach_events ?? 0} />
            <MetricBox label="Outreach Contacts" value={stats?.outreach_contacts ?? 0} />
            <MetricBox label="Counties Served" value={stats?.counties_served ?? 0} />
            <MetricBox label="Resources in Directory" value={stats?.resources_total ?? 0} testId="metric-resources-total" />
            <MetricBox label="Partner Subs · Pending" value={stats?.partner_submissions?.pending_review ?? 0} testId="metric-partner-pending" />
            <MetricBox label="Partner Subs · Approved" value={stats?.partner_submissions?.approved ?? 0} testId="metric-partner-approved" />
            <MetricBox label="Partner Subs · Rejected" value={stats?.partner_submissions?.rejected ?? 0} testId="metric-partner-rejected" />
          </div>
        )}
      </div>
    </div>
  );
}

const ExportCard = ({ icon: Icon, title, description, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="text-left bg-white border border-[#E5E5E0] rounded-2xl p-5 hw-card-hover w-full"
  >
    <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center mb-4">
      <Icon className="w-5 h-5" strokeWidth={1.75} />
    </div>
    <h3 className="font-display font-semibold text-[#0F172A] mb-1">{title}</h3>
    <p className="text-sm text-[#1E3A8A] mb-3">{description}</p>
    <span className="inline-flex items-center gap-1 text-xs font-medium text-[#1E3A8A]">
      <Download className="w-3.5 h-3.5" /> Download
    </span>
  </button>
);

const MetricBox = ({ label, value, testId }) => (
  <div className="bg-[#F9F8F6] rounded-xl p-4" data-testid={testId}>
    <div className="text-2xl font-display font-semibold text-[#0F172A]">{value}</div>
    <div className="text-xs text-[#1E3A8A] mt-1">{label}</div>
  </div>
);

// ─────────────────────── Iter 77 — Executive Daily Brief ──────────────────
function ExecutiveDailyBrief({ exportFile }) {
  const [briefs, setBriefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showDistribution, setShowDistribution] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/exec-brief/list");
      setBriefs(data.briefs || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const askReason = () => {
    const r = window.prompt("Reason for this action (min 20 characters, immutably audited):", "");
    if (r === null) return null;
    if (r.trim().length < 20) { toast.error("Reason must be at least 20 characters."); return null; }
    return r.trim();
  };

  const generateNow = async ({ sendEmail }) => {
    const reason = askReason();
    if (!reason) return;
    setBusy(sendEmail ? "send" : "generate");
    try {
      const { data } = await api.post("/exec-brief/generate", {
        reason, send_email: sendEmail,
      });
      toast.success(sendEmail
        ? `Version ${data.version} generated and emailed to ${(data.delivery?.recipients || []).length} recipient(s).`
        : `Version ${data.version} archived for ${data.brief_date}.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const download = (brief) => {
    exportFile(`/exec-brief/${brief.id}/pdf`,
                brief.pdf_filename || `Exec-Brief-${brief.brief_date}-v${brief.version}.pdf`);
  };

  const openPreview = async (brief) => {
    setBusy(`preview-${brief.id}`);
    try {
      const { data } = await api.get(`/exec-brief/${brief.id}`);
      setPreviewOpen(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const latest = (briefs || [])[0];

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8"
          data-testid="exec-brief-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
          <Sunrise className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display text-xl font-semibold text-[#0F172A]">
                Executive Daily Brief
              </h2>
              <p className="text-sm text-[#334155] mt-1">
                A once-a-day CEO-ready 1–2 page briefing generated at 6:00 AM ET, emailed to the
                Executive Brief Distribution List, and archived here permanently.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowDistribution(true)}
                data-testid="exec-brief-dl-open"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1E3A8A] text-[#1E3A8A] text-sm font-medium hover:bg-[#F1F0EA]"
              >
                <Users className="w-4 h-4" /> Distribution list
              </button>
              <button
                onClick={() => generateNow({ sendEmail: false })}
                disabled={busy === "generate"}
                data-testid="exec-brief-generate"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1E3A8A] text-[#1E3A8A] text-sm font-medium hover:bg-[#F1F0EA] disabled:opacity-60"
              >
                <RefreshCw className={`w-4 h-4 ${busy === "generate" ? "animate-spin" : ""}`} />
                {busy === "generate" ? "Working…" : "Generate now"}
              </button>
              <button
                onClick={() => generateNow({ sendEmail: true })}
                disabled={busy === "send"}
                data-testid="exec-brief-send"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:opacity-90 disabled:opacity-60"
              >
                <Send className="w-4 h-4" />
                {busy === "send" ? "Sending…" : "Send now"}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-[#1E3A8A]"
                  data-testid="exec-brief-loading">Loading briefs…</div>
          ) : !briefs || briefs.length === 0 ? (
            <div className="mt-6 p-4 bg-[#F9F8F6] rounded-xl text-sm text-[#334155]"
                  data-testid="exec-brief-empty">
              No briefs archived yet. The next 6:00 AM ET scheduler run will seed the archive.
              You can also <strong>Generate now</strong> to create a manual version immediately.
            </div>
          ) : (
            <>
              {latest && (
                <div className="mt-4 p-3 border border-[#E5E5E0] rounded-xl bg-[#F9F8F6] flex items-start gap-3"
                      data-testid="exec-brief-latest-panel">
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">Latest brief</div>
                    <div className="font-medium text-[#0F172A]">
                      {latest.brief_date} · v{latest.version} · {latest.source === "scheduler" ? "Scheduled" : "Manual"}
                    </div>
                    <div className="text-xs text-[#334155] mt-1 line-clamp-3">
                      {latest.executive_summary}
                    </div>
                  </div>
                  <button
                    onClick={() => download(latest)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium hover:opacity-90"
                    data-testid={`exec-brief-download-${latest.id}`}
                  >
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </button>
                </div>
              )}

              <div className="mt-4 overflow-x-auto" data-testid="exec-brief-table">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#1E3A8A] border-b border-[#E5E5E0]">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Version</th>
                      <th className="py-2 pr-3">Generated</th>
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3">Delivery</th>
                      <th className="py-2 pr-3">Recipients</th>
                      <th className="py-2 pr-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {briefs.map((b) => (
                      <tr key={b.id} className="border-b border-[#F1F0EA] align-top"
                           data-testid={`exec-brief-row-${b.id}`}>
                        <td className="py-2 pr-3 font-medium text-[#0F172A]">{b.brief_date}</td>
                        <td className="py-2 pr-3 font-mono text-xs">v{b.version}</td>
                        <td className="py-2 pr-3 text-xs text-[#334155]">
                          {(b.generated_at || "").slice(0, 16).replace("T", " ")}
                        </td>
                        <td className="py-2 pr-3">
                          <SourceBadge source={b.source === "scheduler" ? "release_gate" : "live_generated"} />
                        </td>
                        <td className="py-2 pr-3">
                          <DeliveryStatus status={b.delivery_status} />
                        </td>
                        <td className="py-2 pr-3 text-xs text-[#334155]">
                          {b.delivered_to?.length || 0}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => openPreview(b)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F1F0EA] text-[#1E3A8A] text-xs font-medium hover:bg-[#E0E7FF]"
                              data-testid={`exec-brief-preview-${b.id}`}
                            >Preview</button>
                            <button
                              onClick={() => download(b)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F1F0EA] text-[#1E3A8A] text-xs font-medium hover:bg-[#E0E7FF]"
                              data-testid={`exec-brief-download-${b.id}`}
                            >
                              <Download className="w-3 h-3" /> PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {showDistribution && (
        <ExecBriefDistribution onClose={() => setShowDistribution(false)} />
      )}
      {previewOpen && (
        <ExecBriefPreview brief={previewOpen}
                            onClose={() => setPreviewOpen(null)}
                            onDownload={() => download(previewOpen)} />
      )}
    </div>
  );
}

function DeliveryStatus({ status }) {
  if (status === "delivered") return <span className="inline-flex items-center gap-1 text-xs text-emerald-700">Delivered</span>;
  if (status === "failed") return <span className="inline-flex items-center gap-1 text-xs text-red-700">Failed</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-[#94A3B8]">Pending</span>;
}

function ExecBriefPreview({ brief, onClose, onDownload }) {
  const s = brief.sections || {};
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={onClose} data-testid="exec-brief-preview-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E5E5E0] flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#1E3A8A]">Executive Daily Brief</div>
            <h3 className="font-display text-xl font-semibold text-[#0F172A]">
              {brief.brief_date} · v{brief.version}
            </h3>
            <div className="text-xs text-[#64748B] mt-1">
              Generated {brief.generated_at?.slice(0, 16).replace("T", " ")} · {brief.source}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onDownload}
                     className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium">
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
            <button onClick={onClose}
                     className="px-3 py-1.5 rounded-full border border-[#E5E5E0] text-[#334155] text-xs">
              Close
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-3 bg-[#F9F8F6] border-l-4 border-[#1E3A8A] rounded">
            <div className="text-xs uppercase text-[#1E3A8A] mb-1">Executive Summary</div>
            <div className="text-sm text-[#0F172A]">{brief.executive_summary}</div>
          </div>
          <PreviewSection label="Executive KPIs" data={s.executive_kpis} />
          <PreviewSection label="Organizational Activity" data={s.organizational_activity} />
          <PreviewSection label="Partner Ecosystem" data={s.partner_ecosystem} />
          <PreviewSection label="Compliance & Security" data={s.compliance_security} />
          <PreviewSection label="Executive Action Items" data={s.executive_action_items} />
        </div>
      </div>
    </div>
  );
}

function PreviewSection({ label, data }) {
  if (!data) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#1E3A8A] mb-1">{label}</div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-[#F1F0EA] py-1">
            <span className="text-[#64748B]">{k.replace(/_/g, " ")}</span>
            <span className="font-medium text-[#0F172A]">
              {v === null || v === undefined ? "—" :
                typeof v === "object" ? JSON.stringify(v) : String(v)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExecBriefDistribution({ onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ kind: "email", value: "", label: "", enabled: true });

  const load = async () => {
    try {
      const { data } = await api.get("/exec-brief/distribution/list");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const askReason = () => {
    const r = window.prompt("Reason (min 20 chars):", "");
    if (r === null) return null;
    if (r.trim().length < 20) { toast.error("Reason must be at least 20 chars."); return null; }
    return r.trim();
  };

  const addRecipient = async () => {
    const reason = askReason();
    if (!reason) return;
    setBusy("add");
    try {
      await api.post("/exec-brief/distribution/upsert",
        { ...form, reason });
      toast.success("Recipient saved.");
      setForm({ kind: "email", value: "", label: "", enabled: true });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const toggle = async (r) => {
    const reason = askReason();
    if (!reason) return;
    setBusy(r.id);
    try {
      await api.post(`/exec-brief/distribution/${r.id}/toggle`,
        { enabled: !r.enabled, reason });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const remove = async (r) => {
    const reason = askReason();
    if (!reason) return;
    if (!window.confirm(`Remove ${r.value} from the distribution list?`)) return;
    setBusy(r.id);
    try {
      await api.post(`/exec-brief/distribution/${r.id}/delete`, { reason });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={onClose} data-testid="exec-brief-dl-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E5E5E0] flex justify-between">
          <div>
            <h3 className="font-display text-xl font-semibold text-[#0F172A]">
              Executive Brief Distribution List
            </h3>
            <p className="text-sm text-[#334155] mt-1">
              Add roles or specific emails. Role entries are resolved fresh at every delivery.
            </p>
          </div>
          <button onClick={onClose}
                   className="px-3 py-1.5 rounded-full border border-[#E5E5E0] text-[#334155] text-xs h-8">Close</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-4 gap-2 items-end p-3 bg-[#F9F8F6] rounded-xl"
                data-testid="exec-brief-dl-form">
            <label className="text-xs">
              <div className="text-[10px] uppercase text-[#1E3A8A] mb-1">Type</div>
              <select
                value={form.kind}
                onChange={(e) => setForm(f => ({ ...f, kind: e.target.value, value: "" }))}
                className="w-full px-2 py-1.5 rounded border border-[#CBD5E1] text-sm"
                data-testid="exec-brief-dl-kind"
              >
                <option value="email">Email</option>
                <option value="role">Role</option>
              </select>
            </label>
            <label className="text-xs col-span-2">
              <div className="text-[10px] uppercase text-[#1E3A8A] mb-1">
                {form.kind === "email" ? "Email address" : "Role"}
              </div>
              {form.kind === "role" ? (
                <select
                  value={form.value}
                  onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))}
                  className="w-full px-2 py-1.5 rounded border border-[#CBD5E1] text-sm"
                  data-testid="exec-brief-dl-value"
                >
                  <option value="">Select role…</option>
                  {(data?.role_options || []).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              ) : (
                <input
                  type="email"
                  value={form.value}
                  onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))}
                  placeholder="board@example.org"
                  className="w-full px-2 py-1.5 rounded border border-[#CBD5E1] text-sm"
                  data-testid="exec-brief-dl-value"
                />
              )}
            </label>
            <button
              onClick={addRecipient}
              disabled={busy === "add" || !form.value}
              className="px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium disabled:opacity-60"
              data-testid="exec-brief-dl-add"
            >Add</button>
          </div>

          <table className="w-full text-sm border-collapse" data-testid="exec-brief-dl-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#1E3A8A] border-b border-[#E5E5E0]">
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Value</th>
                <th className="py-2 pr-3">Enabled</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recipients || []).map(r => (
                <tr key={r.id} className="border-b border-[#F1F0EA]"
                     data-testid={`exec-brief-dl-row-${r.id}`}>
                  <td className="py-2 pr-3 text-xs uppercase text-[#334155]">{r.kind}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.value}</td>
                  <td className="py-2 pr-3">
                    <button
                      onClick={() => toggle(r)}
                      disabled={busy === r.id}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}
                      data-testid={`exec-brief-dl-toggle-${r.id}`}
                    >{r.enabled ? "ENABLED" : "DISABLED"}</button>
                  </td>
                  <td className="py-2 pr-3">
                    <button
                      onClick={() => remove(r)}
                      disabled={busy === r.id}
                      className="text-xs text-red-700 hover:underline"
                      data-testid={`exec-brief-dl-remove-${r.id}`}
                    >Remove</button>
                  </td>
                </tr>
              ))}
              {(data?.recipients || []).length === 0 && (
                <tr><td colSpan={4} className="py-4 text-center text-sm text-[#64748B]">
                  No recipients yet. Add a role or an email above.
                </td></tr>
              )}
            </tbody>
          </table>

          <div className="p-3 bg-[#F9F8F6] rounded-xl text-xs" data-testid="exec-brief-dl-resolved">
            <div className="uppercase tracking-wider text-[10px] text-[#1E3A8A] mb-1">
              Currently resolves to
            </div>
            <div className="text-[#0F172A]">
              {(data?.resolved_emails || []).length === 0
                ? "No addresses — the 6 AM run will be a no-op."
                : `${data.resolved_emails.length} unique email addresses`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Documentation Archive ────────────────────────
function DocumentationArchive({ exportFile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(null);
  const [showReconfirm, setShowReconfirm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/docs-archive/index");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const askReason = (min = 20) => {
    const r = window.prompt(`Reason for this action (min ${min} characters, immutably audited):`, "");
    if (r === null) return null;
    if (r.trim().length < min) {
      toast.error(`Reason must be at least ${min} characters.`);
      return null;
    }
    return r.trim();
  };

  const regenerate = async () => {
    const reason = askReason(20);
    if (!reason) return;
    const iteration = window.prompt("Iteration label (e.g. Iter77.3):", "Iter77.3");
    if (!iteration) return;
    const summary = window.prompt("Short release-notes summary (optional):", "") || "";
    setBusyAction("regenerate");
    try {
      const { data } = await api.post("/admin/docs-archive/regenerate", {
        iteration, release_notes_summary: summary, reason, force: false,
      });
      toast.success(data.duplicated
        ? "No changes detected — timestamp updated."
        : "New documentation version archived.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyAction(null); }
  };

  const reconstruct = async () => {
    const reason = askReason(20);
    if (!reason) return;
    setBusyAction("reconstruct");
    setShowReconfirm(false);
    try {
      const { data } = await api.post("/admin/docs-archive/reconstruct-history", { reason });
      toast.success(
        `Reconstruction complete — ${data.reconstructed?.length || 0} rebuilt, ${data.unavailable?.length || 0} unavailable.`,
      );
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyAction(null); }
  };

  const retryCommit = async (entryId) => {
    const reason = askReason(20);
    if (!reason) return;
    setBusyAction(entryId);
    try {
      const { data } = await api.post(`/admin/docs-archive/${encodeURIComponent(entryId)}/commit-git`, { reason });
      if (data.ok) toast.success("Git commit succeeded.");
      else toast.error(`Git commit still pending: ${data.git?.reason || "unknown"}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyAction(null); }
  };

  const download = (relPath, filename) => {
    exportFile(
      `/admin/docs-archive/download?path=${encodeURIComponent(relPath)}`,
      filename,
    );
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8" data-testid="documentation-archive-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
          <Archive className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display text-xl font-semibold text-[#0F172A]">Documentation Archive</h2>
              <p className="text-sm text-[#334155] mt-1">
                Every capability document is automatically versioned, hashed, and git-committed after each completed iteration.
                Historical entries reconstructed from git history are clearly marked.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={regenerate}
                disabled={busyAction === "regenerate"}
                data-testid="docs-archive-regenerate"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:opacity-90 disabled:opacity-60"
              >
                <RefreshCw className={`w-4 h-4 ${busyAction === "regenerate" ? "animate-spin" : ""}`} />
                {busyAction === "regenerate" ? "Working…" : "Regenerate now"}
              </button>
              <button
                onClick={() => setShowReconfirm(true)}
                disabled={busyAction === "reconstruct"}
                data-testid="docs-archive-reconstruct-open"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1E3A8A] text-[#1E3A8A] text-sm font-medium hover:bg-[#F1F0EA] disabled:opacity-60"
              >
                <History className="w-4 h-4" />
                Reconstruct history
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-[#1E3A8A]" data-testid="docs-archive-loading">Loading archive…</div>
          ) : !data || data.entries?.length === 0 ? (
            <div className="mt-6 p-4 bg-[#F9F8F6] rounded-xl text-sm text-[#334155]" data-testid="docs-archive-empty">
              No archived versions yet. Click <strong>Regenerate now</strong> to create the first snapshot, or
              <strong> Reconstruct history</strong> to seed the archive from Git history.
            </div>
          ) : (
            <>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="docs-archive-counts">
                <CountPill label="Total" value={data.counts?.total || 0} />
                <CountPill label="Committed" value={data.counts?.committed || 0} tone="ok" />
                <CountPill label="Pending commit" value={data.counts?.pending_git_commit || 0} tone={(data.counts?.pending_git_commit || 0) > 0 ? "warn" : "muted"} />
                <CountPill label="Reconstructed" value={data.counts?.reconstructed || 0} tone="muted" />
              </div>

              {(data.counts?.pending_git_commit || 0) > 0 && (
                <div className="mt-4 flex items-start gap-3 p-3 border border-amber-300 bg-amber-50 rounded-xl text-sm text-amber-900" data-testid="docs-archive-pending-banner">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>{data.counts.pending_git_commit}</strong> archive entr{data.counts.pending_git_commit === 1 ? "y is" : "ies are"} saved locally but not yet git-committed.
                    Use the <em>Retry commit</em> button on each row, or push via <strong>Save to GitHub</strong> in the platform toolbar.
                  </div>
                </div>
              )}

              <div className="mt-4 overflow-x-auto" data-testid="docs-archive-table">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#1E3A8A] border-b border-[#E5E5E0]">
                      <th className="py-2 pr-3">Iteration</th>
                      <th className="py-2 pr-3">Semver</th>
                      <th className="py-2 pr-3">Release</th>
                      <th className="py-2 pr-3">Commit</th>
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3">Git</th>
                      <th className="py-2 pr-3">Downloads</th>
                      <th className="py-2 pr-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries.map((e) => (
                      <tr key={e.id} className="border-b border-[#F1F0EA] align-top" data-testid={`docs-archive-row-${e.id}`}>
                        <td className="py-2 pr-3 font-medium text-[#0F172A]">{e.iteration}</td>
                        <td className="py-2 pr-3 font-mono text-xs text-[#334155]">{e.semantic_version}</td>
                        <td className="py-2 pr-3 text-[#334155]">{e.release_date}</td>
                        <td className="py-2 pr-3 font-mono text-xs text-[#334155]">{e.git_commit}</td>
                        <td className="py-2 pr-3"><SourceBadge source={e.source} /></td>
                        <td className="py-2 pr-3"><CommitStatus status={e.git_commit_status} sha={e.git_commit_sha} /></td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-1">
                            {e.summary_pdf && (
                              <DownloadLink onClick={() => download(e.summary_pdf, `Capability-Summary-${e.iteration}.pdf`)} testId={`docs-archive-dl-summary-${e.id}`} label="Summary" />
                            )}
                            {e.register_pdf && (
                              <DownloadLink onClick={() => download(e.register_pdf, `Capabilities-Register-${e.iteration}.pdf`)} testId={`docs-archive-dl-register-${e.id}`} label="Register" />
                            )}
                            {e.register_md && (
                              <DownloadLink onClick={() => download(e.register_md, `Capabilities-Register-${e.iteration}.md`)} testId={`docs-archive-dl-md-${e.id}`} label="MD" />
                            )}
                            {!e.summary_pdf && !e.register_pdf && !e.register_md && (
                              <span className="text-xs text-[#94A3B8] italic">no artifact</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          {e.git_commit_status === "pending_git_commit" && (
                            <button
                              onClick={() => retryCommit(e.id)}
                              disabled={busyAction === e.id}
                              data-testid={`docs-archive-retry-${e.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-amber-400 text-amber-700 text-xs font-medium hover:bg-amber-50 disabled:opacity-60"
                            >
                              <GitCommit className="w-3 h-3" /> Retry commit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data.entries.length > 0 && data.entries[0].release_notes_summary && (
                <div className="mt-4 p-3 bg-[#F9F8F6] rounded-xl text-xs text-[#334155]" data-testid="docs-archive-latest-notes">
                  <div className="uppercase tracking-wider text-[10px] text-[#1E3A8A] mb-1">Latest release notes</div>
                  <div className="line-clamp-4">{data.entries[0].release_notes_summary}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showReconfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowReconfirm(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="docs-archive-reconstruct-modal">
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">Reconstruct history?</h3>
            <p className="text-sm text-[#334155] mt-2">
              This is a <strong>one-time migration</strong> that walks every PRD.md milestone and rebuilds
              the Capability Summary at the introducing git commit. Reconstructed documents are labeled and can
              never be treated as original artifacts. Safe to re-run — content-hash dedup ensures nothing duplicates.
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <button
                onClick={() => setShowReconfirm(false)}
                className="px-4 py-2 rounded-full border border-[#E5E5E0] text-[#334155] text-sm"
                data-testid="docs-archive-reconstruct-cancel"
              >Cancel</button>
              <button
                onClick={reconstruct}
                disabled={busyAction === "reconstruct"}
                className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-60"
                data-testid="docs-archive-reconstruct-confirm"
              >{busyAction === "reconstruct" ? "Working…" : "Reconstruct now"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CountPill = ({ label, value, tone = "default" }) => {
  const toneCls = {
    ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warn: "bg-amber-50 text-amber-900 border-amber-300",
    muted: "bg-[#F9F8F6] text-[#334155] border-[#E5E5E0]",
    default: "bg-[#EEF2FF] text-[#1E3A8A] border-[#C7D2FE]",
  }[tone] || "bg-[#EEF2FF] text-[#1E3A8A] border-[#C7D2FE]";
  return (
    <div className={`px-3 py-2 rounded-xl border text-center ${toneCls}`}>
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
};

const SourceBadge = ({ source }) => {
  const map = {
    release_gate: { label: "Release", cls: "bg-[#1E3A8A] text-white" },
    live_generated: { label: "Live", cls: "bg-[#7E937D] text-white" },
    reconstructed: { label: "Historical", cls: "bg-[#F9F8F6] text-[#334155] border border-[#E5E5E0]" },
  };
  const cfg = map[source] || { label: source, cls: "bg-[#F9F8F6] text-[#334155]" };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.cls}`}>{cfg.label}</span>;
};

const CommitStatus = ({ status, sha }) => {
  if (status === "committed") return <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><GitCommit className="w-3 h-3" /> {sha || "committed"}</span>;
  if (status === "pending_git_commit") return <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="w-3 h-3" /> pending</span>;
  return <span className="text-xs text-[#94A3B8]">—</span>;
};

const DownloadLink = ({ onClick, testId, label }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F1F0EA] text-[#1E3A8A] text-xs font-medium hover:bg-[#E0E7FF]"
  >
    <Download className="w-3 h-3" /> {label}
  </button>
);

const SERIES_META = [
  { key: "care_seekers", label: "Care Seekers", color: "#1E3A8A" },
  { key: "intakes", label: "Intakes", color: "#7E937D" },
  { key: "referrals", label: "Referrals", color: "#92400E" },
  { key: "notes", label: "Wellness Notes", color: "#1E40AF" },
  { key: "appointments", label: "Appointments", color: "#166534" },
  { key: "outreach", label: "Outreach", color: "#9333EA" },
  { key: "partner_submissions", label: "Partner submissions", color: "#DC2626" },
];

function TrendsSection() {
  const [data, setData] = useState(null);
  const [windowMonths, setWindowMonths] = useState(12);
  const [enabled, setEnabled] = useState(() => Object.fromEntries(SERIES_META.map((s) => [s.key, true])));
  const [chartType, setChartType] = useState("line");

  useEffect(() => {
    let cancelled = false;
    api.get("/reports/trends", { params: { months: windowMonths } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => toast.error(formatApiError(e)));
    return () => { cancelled = true; };
  }, [windowMonths]);

  if (!data) return null;

  // Pretty-format YYYY-MM → "Jan '26"
  const fmtMonth = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${names[m - 1]} '${String(y).slice(2)}`;
  };

  const rows = data.months.map((ym, i) => {
    const r = { month: fmtMonth(ym) };
    for (const s of SERIES_META) r[s.key] = (data.series[s.key] || [])[i] || 0;
    return r;
  });

  const ChartComp = chartType === "bar" ? BarChart : LineChart;
  const SeriesComp = chartType === "bar" ? Bar : Line;

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5 space-y-4" data-testid="trends-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">Trends</h2>
          <p className="text-xs text-[#1E3A8A] mt-0.5">
            Monthly activity over the last {windowMonths} months. Toggle series below.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-[#F9F8F6] border border-[#E5E5E0] rounded-full p-1 text-xs">
            {[6, 12, 24].map((m) => (
              <button
                key={m}
                onClick={() => setWindowMonths(m)}
                data-testid={`trends-window-${m}`}
                className={`px-3 py-1 rounded-full transition-colors ${windowMonths === m ? "bg-[#1E3A8A] text-white" : "text-[#334155]"}`}
              >
                {m}m
              </button>
            ))}
          </div>
          <div className="inline-flex bg-[#F9F8F6] border border-[#E5E5E0] rounded-full p-1 text-xs">
            <button
              onClick={() => setChartType("line")}
              data-testid="trends-line-btn"
              className={`px-3 py-1 rounded-full transition-colors ${chartType === "line" ? "bg-[#1E3A8A] text-white" : "text-[#334155]"}`}
            >
              Line
            </button>
            <button
              onClick={() => setChartType("bar")}
              data-testid="trends-bar-btn"
              className={`px-3 py-1 rounded-full transition-colors ${chartType === "bar" ? "bg-[#1E3A8A] text-white" : "text-[#334155]"}`}
            >
              Bar
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="series-toggles">
        {SERIES_META.map((s) => {
          const on = !!enabled[s.key];
          return (
            <button
              key={s.key}
              onClick={() => setEnabled((prev) => ({ ...prev, [s.key]: !on }))}
              data-testid={`series-toggle-${s.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                on ? "border-transparent text-white" : "bg-white text-[#334155] border-[#E5E5E0]"
              }`}
              style={on ? { backgroundColor: s.color } : undefined}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: s.color, opacity: on ? 1 : 0.5 }}
              />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="w-full h-72" data-testid="trends-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ChartComp data={rows} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
            <CartesianGrid stroke="#F0EFEB" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#1E3A8A" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#1E3A8A" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "#fff", border: "1px solid #E5E5E0", borderRadius: 12, fontSize: 12 }}
              labelStyle={{ color: "#0F172A", fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {SERIES_META.filter((s) => enabled[s.key]).map((s) => (
              chartType === "bar" ? (
                <SeriesComp
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  fill={s.color}
                  radius={[6, 6, 0, 0]}
                />
              ) : (
                <SeriesComp
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 5 }}
                />
              )
            ))}
          </ChartComp>
        </ResponsiveContainer>
      </div>
    </div>
  );
}


